<template>
  <div class="p-4 w-full max-h-full overflow-y-scroll bg-[#f7f7f7]">
    <div
      class="h-full bg-white p-6 rounded-lg shadow-sm w-full overflow-y-scroll border-[1px] border-gray-300"
    >
      <div v-if="applicationsStore.isLoadingSelected">
        <!-- Buttons -->
        <div class="flex items-center w-fit float-right">
          <router-link
            :to="{ name: 'User Applications' }"
            class="ml-2 cursor-pointer text-sm aspect-square h-9 hover:bg-gray-100 transition-all duration-200 rounded-full"
          >
            <Button variant="ghost" size="icon" class="h-9 w-9">
              <X class="inline-block h-5" />
            </Button>
          </router-link>
        </div>
        <div class="flex items-center justify-center h-full min-h-72">
          <LoaderCircle class="animate-spin mr-2" />Loading...
        </div>
      </div>

      <div v-else>
        <div class="flex items-center w-full">
          <router-link
            :to="{
              name: 'Job Application container',
              params: { application_id: selectedApplication?.id }
            }"
            class="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 transition-all duration-200 rounded-md"
          >
            <ArrowLeft class="h-4 w-4 flex-shrink-0" />
            <span class="font-medium">
              {{ selectedApplication?.user_details?.first_name }}
              {{ selectedApplication?.user_details?.last_name }}'s application
            </span>
          </router-link>
        </div>

        <!-- Container signifying persisnent role data -->
        <div class="bg-gray-50 p-4 rounded-lg mt-2">
          <!-- Main information -->
          <div class="text-md mb-2 w-full text-lg">
            <div>
              <h2 class="inline-block font-semibold">
                {{ selectedApplication?.open_role_details?.role_name }}
              </h2>
              •
              <span class="inline underline">
                {{ selectedApplication?.open_role_details?.employer_details?.name }}
              </span>
            </div>
          </div>

          <span class="flex flex-wrap gap-2 text-sm items-center mt-1 text-gray-600">
            <span
              class="inline-flex items-center gap-1 px-2 bg-gray-100 rounded-full border-[1px] border-gray-300"
              v-for="location in selectedApplication?.open_role_details?.locations_details"
              :key="location?.id"
            >
              <MapPin class="w-3 h-3 mr-[2px] text-xs" />
              {{ location?.location_name as string }}
            </span>
          </span>

          <!-- Status signifiers -->
          <div class="flex items-center text-sm flex-wrap mt-4 ml-2">
            <!-- Job status -->
            <div class="flex items-center w-40">
              <span
                class="h-2 w-2 rounded-full mr-2 animate-pulse duration-900"
                :style="{
                  backgroundColor: selectedApplication?.open_role_details?.status_open
                    ? '#22bb45'
                    : 'red'
                }"
              ></span>
              <span class="font-semibold mr-1">Job status: </span>
              <span
                class="text-gray-600 block text-sm capitalize"
                :style="{
                  color: selectedApplication?.open_role_details?.status_open ? '#22bb45' : 'red'
                }"
                >{{ selectedApplication?.open_role_details?.status_open ? 'Open' : 'Closed' }}</span
              >
            </div>

            <!-- Your status -->
            <div class="flex text-sm items-center relative">
              <StatusDropdown
                :current-status="selectedApplication?.status || ''"
                :application-id="selectedApplication?.id || ''"
              />
            </div>
          </div>

          <hr class="w-full mt-4" />

          <!-- Role URL -->
          <div class="text-sm mt-2 p-1 cursor-default flex items-center">
            <span class="font-semibold min-w-44 flex items-center">
              <Link class="w-4 h-4 mr-2" /> Role post URL:
            </span>
            <a
              :href="selectedApplication?.open_role_details?.url"
              target="_blank"
              class="underline hover:text-blue-600 transition-colors transition-duration-200 truncate"
              >{{ selectedApplication?.open_role_details?.url }}</a
            >
          </div>

          <!-- Seniority -->
          <div class="text-sm mt-1 p-1 cursor-default flex items-center">
            <span class="font-semibold w-44 flex items-center">
              <ChartColumnBig class="w-4 h-4 mr-2" />Seniority:</span
            >
            <SeniorityLabel
              :seniority-level="selectedApplication?.open_role_details?.seniority_level || ''"
              class="-ml-1"
            />
          </div>

          <!-- Local authorization required -->
          <div
            v-if="selectedApplication?.open_role_details?.work_authorization_requirement"
            class="text-sm mt-1 p-1 cursor-default flex items-center"
          >
            <span class="font-semibold w-44 flex items-center">
              <EarthLock class="w-4 h-4 mr-2" />Local authorization:</span
            >

            <span>{{
              authorizationRequirements.find(
                (requirement) =>
                  requirement.id === selectedApplication?.open_role_details?.work_authorization_requirement
              )?.label
            }}</span>
          </div>

          <div class="text-sm mt-1 p-1 cursor-default flex items-center">
            <span class="font-semibold w-44 flex items-center">
              <User class="w-4 h-4 mr-2" />Applicant:</span
            >
            <span
              >{{ selectedApplication?.user_details?.first_name }}
              {{ selectedApplication?.user_details?.last_name }}</span
            >
          </div>
        </div>

        <hr class="w-full mt-4" />

        <Form v-model="mainForm" @submit="handleSubmit" class="mt-4 space-y-4">
          <!-- Referrer fields -->
          <FormField v-if="isGuide" name="referrer">
            <FormItem>
              <FormLabel class="font-semibold flex items-center">
                <UserRoundCheck class="w-4 h-4 mr-2" />Referrer:
              </FormLabel>
              <FormControl>
                <Select v-model="mainForm.referrer" :disabled="referrersStore.isLoading">
                  <SelectTrigger>
                    <SelectValue placeholder="Select referrer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup v-for="group in groupedReferrers" :key="group.label">
                      <SelectLabel>{{ group.label }}</SelectLabel>
                      <SelectItem
                        v-for="item in group.items"
                        :key="item.value ?? 'null'"
                        :value="item.value"
                        :disabled="!!item.disabled"
                      >
                        {{ item.label }}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-if="isGuide" name="recruiters">
            <FormItem>
              <FormLabel class="font-semibold flex items-center">
                <MailCheck class="w-4 h-4 mr-2" />Recruiter(s):
              </FormLabel>
              <FormControl>
                <Select v-model="mainForm.recruiters" multiple :disabled="recruitersStore.isLoading">
                  <SelectTrigger>
                    <SelectValue placeholder="Select recruiters" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem
                        v-for="recruiter in recruitersStore.recruiters"
                        :key="recruiter.id"
                        :value="recruiter.id"
                      >
                        {{ recruiter.name }}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </FormControl>
              <div class="flex flex-wrap gap-1 mt-1">
                <span
                  v-for="recruiter in mainForm?.recruiters ?? []"
                  :key="recruiter"
                  class="text-xs bg-gray-100 rounded-full px-2 py-1 font-normal flex items-center gap-1"
                >
                  {{ recruitersStore.recruiters.find((object) => object.id === recruiter)?.name }}
                  <button
                    @click="
                      mainForm.recruiters = mainForm.recruiters.filter((id) => id !== recruiter)
                    "
                    class="hover:text-red-500 transition-colors ml-1"
                    type="button"
                  >
                    ×
                  </button>
                </span>
              </div>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField name="comments">
            <FormItem>
              <FormLabel class="font-semibold flex items-center">
                <MessageSquareMore class="w-4 h-4 mr-2" />Comments:
              </FormLabel>
              <FormControl>
                <Textarea v-model="mainForm.comments" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </Form>

        <!-- Timeline -->
        <div class="mt-4 text-sm rounded-lg w-full cursor-default border-[1px] border-gray-200">
          <button
            @click="timelineToggle = !timelineToggle"
            class="font-semibold flex items-center justify-between w-full hover:bg-gray-100 transition-all duration-200 p-4"
          >
            <div class="flex items-center">
              <CalendarDays class="w-4 h-4 mr-2 transition-transform duration-200" />
              <span>Timeline</span>
            </div>
            <ChevronDown
              class="w-4 h-4 transition-transform duration-200"
              :style="{ transform: timelineToggle ? 'rotate(180deg)' : 'rotate(0deg)' }"
            />
          </button>

          <!-- Timeline form -->
          <transition
            enter-active-class="transition-all duration-300 ease-out"
            leave-active-class="transition-all duration-300 ease-in"
            enter-from-class="transform opacity-0 max-h-0"
            enter-to-class="transform opacity-100 max-h-[1000px]"
            leave-from-class="transform opacity-100 max-h-[1000px]"
            leave-to-class="transform opacity-0 max-h-0"
          >
            <div v-if="timelineToggle" class="px-4 pb-2">
              <Form v-model="timelineForm" class="w-full p-2 space-y-4">
                <div class="grid grid-cols-2 gap-4">
                  <FormField name="date_referral_requested">
                    <FormItem>
                      <FormLabel>Referral requested</FormLabel>
                      <FormControl>
                        <DatePicker v-model="timelineForm.date_referral_requested" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>

                  <FormField name="date_referral_confirmed">
                    <FormItem>
                      <FormLabel>Referral confirmed</FormLabel>
                      <FormControl>
                        <DatePicker v-model="timelineForm.date_referral_confirmed" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>

                  <FormField name="date_coding_test">
                    <FormItem>
                      <FormLabel>Coding test</FormLabel>
                      <FormControl>
                        <DatePicker v-model="timelineForm.date_coding_test" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>

                  <FormField name="date_first_call">
                    <FormItem>
                      <FormLabel>First call</FormLabel>
                      <FormControl>
                        <DatePicker v-model="timelineForm.date_first_call" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>

                  <FormField name="date_onsite_invited">
                    <FormItem>
                      <FormLabel>Onsite invited</FormLabel>
                      <FormControl>
                        <DatePicker v-model="timelineForm.date_onsite_invited" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>

                  <FormField name="date_onsite_result">
                    <FormItem>
                      <FormLabel>Onsite result</FormLabel>
                      <FormControl>
                        <DatePicker v-model="timelineForm.date_onsite_result" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>

                  <FormField name="date_offer_received">
                    <FormItem>
                      <FormLabel>Offer received</FormLabel>
                      <FormControl>
                        <DatePicker v-model="timelineForm.date_offer_received" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>

                  <FormField name="date_offer_signed">
                    <FormItem>
                      <FormLabel>Offer signed</FormLabel>
                      <FormControl>
                        <DatePicker v-model="timelineForm.date_offer_signed" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </FormField>
                </div>
              </Form>
            </div>
          </transition>
        </div>
      </div>

      <div v-if="applicationsStore.error" class="block w-full mt-4 text-red-500 text-sm text-right">
        {{ applicationsStore.error }}
      </div>

      <div
        v-if="!selectedApplication?.open_role_details?.status_open"
        class="w-full mt-4 text-sm text-right flex items-center justify-end text-red-500"
      >
        <TriangleAlert class="w-4 h-4 inline mr-1" /> Application cannot be edited because the role
        has been closed
      </div>

      <div class="flex items-center justify-end gap-2 mt-8">
        <Button @click="deleteApplication" variant="destructive" class="mr-auto bg-transparent border-red-500 border-[1px] text-red-500 hover:text-white">
          Delete application
        </Button>
        <Button @click="goBack" variant="outline" class="w-20">Cancel</Button>
        <Button
          @click="handleSubmit"
          :disabled="!selectedApplication?.open_role_details?.status_open"
          class="w-20 bg-cosmicPurple hover:bg-cosmicPurple/80 hover:translate-y-[-1px] transition-all duration-200"
          :class="{ 'opacity-50': !selectedApplication?.open_role_details?.status_open }"
          :title="!selectedApplication?.open_role_details?.status_open ? 'Role is closed' : ''"
        >
          {{ applicationsStore.isLoadingSelected ? 'Saving...' : 'Save' }}
        </Button>
      </div>

      <JobApplicationDelete
        v-if="deleting"
        :application-id="applicationID"
        @close="deleting = false"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  X,
  Link,
  CalendarDays,
  MessageSquareMore,
  ChartColumnBig,
  LoaderCircle,
  ChevronDown,
  TriangleAlert,
  User,
  EarthLock,
  MapPin,
  UserRoundCheck,
  MailCheck,
  ArrowLeft
} from 'lucide-vue-next'
import { useApplicationsStore } from '@/stores/applications'
import StatusDropdown from '../common/StatusDropdown.vue'
import SeniorityLabel from '../common/SeniorityLabel.vue'
import JobApplicationDelete from './JobApplicationDelete.vue'
import authorizationRequirements from '@/const/authorizationRequirements'
import { useAuth } from '@/composables/useAuth'
import { useReferrersStore } from '@/stores/referrers'
import { useRecruitersStore } from '@/stores/recruiters'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel
} from '@/components/ui/select'
import DatePicker from '@/components/ui/date-picker/DatePicker.vue'
import { Button } from '@/components/ui/button'

const { isGuide } = useAuth()

const route = useRoute()
const router = useRouter()

const applicationsStore = useApplicationsStore()
const applicationID = ref(route.params.application_id as string)
const selectedApplication = computed(() => applicationsStore.selectedApplication)

const referrersStore = useReferrersStore()
const recruitersStore = useRecruitersStore()
const timelineToggle = ref(false)
const deleting = ref(false)

const mainForm = ref({
  comments: '',
  referrer: null as number | null,
  recruiters: [] as number[]
})

// Update the form values when the selectedApplication changes
watch(
  () => selectedApplication.value,
  (newVal) => {
    if (newVal) {
      mainForm.value = {
        comments: newVal.comments ?? '',
        referrer: newVal.referrer_details?.id ?? null,
        recruiters: (newVal.recruiters ?? []).map(r => r.id)
      }
    }
  },
  { immediate: true }
)

const timelineForm = ref({
  date_offer_signed: undefined as Date | undefined,
  date_offer_received: undefined as Date | undefined,
  date_onsite_result: undefined as Date | undefined,
  date_onsite_invited: undefined as Date | undefined,
  date_first_call: undefined as Date | undefined,
  date_coding_test: undefined as Date | undefined,
  date_referral_confirmed: undefined as Date | undefined,
  date_referral_requested: undefined as Date | undefined
})

// Update timeline form when selectedApplication changes
watch(
  () => selectedApplication.value,
  (newVal) => {
    if (newVal) {
      timelineForm.value = {
        date_offer_signed: newVal.date_offer_signed ? new Date(newVal.date_offer_signed) : undefined,
        date_offer_received: newVal.date_offer_received ? new Date(newVal.date_offer_received) : undefined,
        date_onsite_result: newVal.date_onsite_result ? new Date(newVal.date_onsite_result) : undefined,
        date_onsite_invited: newVal.date_onsite_invited ? new Date(newVal.date_onsite_invited) : undefined,
        date_first_call: newVal.date_first_call ? new Date(newVal.date_first_call) : undefined,
        date_coding_test: newVal.date_coding_test ? new Date(newVal.date_coding_test) : undefined,
        date_referral_confirmed: newVal.date_referral_confirmed ? new Date(newVal.date_referral_confirmed) : undefined,
        date_referral_requested: newVal.date_referral_requested ? new Date(newVal.date_referral_requested) : undefined
      }
    }
  },
  { immediate: true }
)

onMounted(async () => {
  try {
    await applicationsStore.fetchApplicationById(applicationID.value)
    await referrersStore.fetchReferrers()
    await recruitersStore.fetchRecruiters()
  } catch (error) {
    console.error('Failed to fetch application:', error)
  }
})

const groupedReferrers = computed(() => {
  const referrers = referrersStore.referrers || []
  const employerId = selectedApplication.value?.open_role_details?.employer
  const employerName = selectedApplication.value?.open_role_details?.employer_details?.name

  const activeReferrers = referrers.filter((referrer) => referrer.is_active)

  const companyRefs = activeReferrers
    .filter((referrer) => referrer.employers?.includes(employerId as number))
    .map((referrer) => ({
      label: referrer.name,
      value: referrer.id,
      disabled: false
    }))

  const otherRefs = activeReferrers
    .filter((referrer) => !referrer.employers?.includes(employerId as number))
    .map((referrer) => ({
      label: referrer.name,
      value: referrer.id,
      disabled: false
    }))

  return [
    {
      label: `Referrers at ${employerName}`,
      items: companyRefs.length
        ? companyRefs
        : [{ label: `No ${employerName} referrers available`, value: null, disabled: true }]
    },
    {
      label: 'Other Referrers',
      items: otherRefs.length
        ? otherRefs
        : [{ label: 'No other referrers available', value: null, disabled: true }]
    }
  ]
})

async function handleSubmit() {
  try {
    const payload = {
      ...mainForm.value,
      ...Object.fromEntries(
        Object.entries(timelineForm.value).map(([key, value]) => [
          key,
          value instanceof Date ? value.toISOString().split('T')[0] : null
        ])
      )
    }
    await applicationsStore.updateApplication(applicationID.value, payload as any)
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push(`/jobs/${route.params.role_id}/${applicationID.value}`)
  } catch (error) {
    console.error('Failed to update application:', error)
  }
}

const deleteApplication = async () => {
  deleting.value = true
}

const goBack = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(`/jobs/${route.params.role_id}/${applicationID.value}`)
}
</script>

<style></style>
